import { z } from "zod";
import { Id, IsoDateTime, Page, PageQuery } from "./common";

// ---------------------------------------------------------------------------
// Typed integration configuration (replaces z.record(z.unknown()))
// ---------------------------------------------------------------------------

/** Simulator adapter configuration stored in IntegrationConnection.config. */
export const SimulatorIntegrationConfig = z.object({
  integrationType: z.literal("Simulator"),
  /** Base URL of the simulator HTTP API (e.g. https://xyz.execute-api.us-east-1.amazonaws.com). */
  baseUrl: z.string().url(),
  /** External tenant identifier as the simulator sees it (not platform tenantId). */
  externalTenantId: z.string().min(1),
  /** Whether the poll worker should poll this integration on the scheduler cadence. */
  pollingEnabled: z.boolean().default(true),
  /** Override polling interval in seconds (default: 60). */
  pollingIntervalSeconds: z.number().int().min(5).default(60),
  /** Default scenario profile to use when activating scenarios on this integration. */
  scenarioProfileId: z.string().optional(),
});
export type SimulatorIntegrationConfig = z.infer<typeof SimulatorIntegrationConfig>;

/** Generic/manufacturer adapter configuration for non-simulator integrations. */
export const GenericIntegrationConfig = z.object({
  integrationType: z.enum(["Manufacturer", "Manual"]),
  /** URL for the poll endpoint on the external provider. */
  pollUrl: z.string().url().optional(),
  /** URL for the command dispatch endpoint. */
  commandUrl: z.string().url().optional(),
  pollingEnabled: z.boolean().default(true),
  pollingIntervalSeconds: z.number().int().min(5).default(60),
});
export type GenericIntegrationConfig = z.infer<typeof GenericIntegrationConfig>;

/** Discriminated union of all integration config shapes. */
export const IntegrationConfig = z.discriminatedUnion("integrationType", [
  SimulatorIntegrationConfig,
  GenericIntegrationConfig,
]);
export type IntegrationConfig = z.infer<typeof IntegrationConfig>;

export const IntegrationType = z.enum(["Simulator", "Manufacturer", "Manual"]);
export type IntegrationType = z.infer<typeof IntegrationType>;

export const IntegrationStatus = z.enum([
  "Connected",
  "Degraded",
  "Disconnected",
  "Disabled",
]);
export type IntegrationStatus = z.infer<typeof IntegrationStatus>;

export const IntegrationEventType = z.enum([
  "HealthCheck",
  "ForceRefreshRequested",
  "ForceRefreshCompleted",
  "PollSucceeded",
  "PollFailed",
  "MappingCreated",
  "MappingFailed",
  "ConnectionDisabled",
  "ConnectionEnabled",
]);
export type IntegrationEventType = z.infer<typeof IntegrationEventType>;

export const ExternalEntityType = z.enum(["Site", "Asset", "Device"]);
export type ExternalEntityType = z.infer<typeof ExternalEntityType>;

/**
 * Lifecycle state of an ExternalIdMapping record.
 *
 * Unmapped:   External entity seen in poll but not yet linked to an internal entity.
 * Linked:     Operator linked the external entity to an existing internal entity.
 * Rejected:   Operator rejected this external entity; suppress from unmapped queue.
 * Superseded: Mapping replaced by a newer mapping for the same externalId.
 * Disabled:   Temporarily inactive (e.g. device decommissioned on provider side).
 * Merged:     Two mappings for the same device were merged by a bulk-map operation.
 */
export const MappingLifecycleState = z.enum([
  "Linked",
  "Rejected",
  "Superseded",
  "Disabled",
  "Merged",
]);
export type MappingLifecycleState = z.infer<typeof MappingLifecycleState>;

export const IntegrationConnection = z.object({
  integrationId: Id,
  tenantId: Id,
  name: z.string().min(1).max(160),
  integrationType: IntegrationType,
  /** Certified adapter identifier from the AdapterRegistry (e.g. "conatus-generic-rest"). */
  adapterId: z.string().min(1).max(120).nullable().optional(),
  /** Exact certified adapter version (semver) that this connection targets. */
  adapterVersion: z.string().min(1).max(50).nullable().optional(),
  status: IntegrationStatus,
  /**
   * Typed configuration for the integration. Use IntegrationConfig discriminated union
   * for full type safety. Falls back to z.record(z.unknown()) for backward compat.
   */
  config: z.union([IntegrationConfig, z.record(z.unknown())]),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  createdBy: Id.nullable(),
  updatedBy: Id.nullable(),
  /**
   * API-only: simulator HTTP origin used by poll / commands for this connection.
   * Matches backend resolution (SIMULATOR_BASE_URL overrides config.baseUrl).
   */
  resolvedSimulatorBaseUrl: z.string().url().optional(),
});
export type IntegrationConnection = z.infer<typeof IntegrationConnection>;

export const IntegrationHealth = z.object({
  integrationId: Id,
  tenantId: Id,
  status: IntegrationStatus,
  lastSuccessAt: IsoDateTime.nullable(),
  lastFailureAt: IsoDateTime.nullable(),
  consecutiveFailureCount: z.number().int().min(0),
  message: z.string().max(2000).nullable(),
  updatedAt: IsoDateTime,

  // -------------------------------------------------------------------------
  // Credential, certificate, and protocol health dimensions
  // These fields are optional / nullable so existing records remain valid.
  // -------------------------------------------------------------------------

  /** Count of authentication failures (401/403) since last success. */
  authFailureCount: z.number().int().min(0).default(0),
  /** When the most recent rate-limit (429) was encountered. */
  rateLimitHitAt: IsoDateTime.nullable().optional(),
  /** When the adapter credential (API key / OAuth token / shared secret) expires. */
  credentialExpiresAt: IsoDateTime.nullable().optional(),
  /** When the TLS/mTLS certificate for this integration expires. */
  certExpiresAt: IsoDateTime.nullable().optional(),
  /** Count of payload validation failures (contract schema errors) in this cycle. */
  validationFailureCount: z.number().int().min(0).default(0),
  /** Count of unmapped external entities seen in the most recent poll response. */
  unmappedEntityCount: z.number().int().min(0).default(0),
  /** Count of auto-retried poll attempts in the most recent cycle. */
  retryCount: z.number().int().min(0).default(0),
  /** When a partner-reported outage condition was first detected. */
  partnerOutageAt: IsoDateTime.nullable().optional(),
  /** When the partner outage was resolved (null = still ongoing). */
  partnerOutageResolvedAt: IsoDateTime.nullable().optional(),
});
export type IntegrationHealth = z.infer<typeof IntegrationHealth>;

export const IntegrationEvent = z.object({
  integrationEventId: Id,
  integrationId: Id,
  tenantId: Id,
  eventType: IntegrationEventType,
  status: IntegrationStatus.nullable(),
  message: z.string().max(2000).nullable(),
  payload: z.record(z.unknown()).nullable(),
  occurredAt: IsoDateTime,
  createdAt: IsoDateTime,
});
export type IntegrationEvent = z.infer<typeof IntegrationEvent>;

export const ExternalIdMapping = z.object({
  externalIdMappingId: Id,
  tenantId: Id,
  integrationId: Id,
  externalEntityType: ExternalEntityType,
  externalId: z.string().min(1).max(256),
  internalEntityId: Id,
  /** Lifecycle state of this mapping. Null treated as Linked for backward compat. */
  lifecycleState: MappingLifecycleState.nullable().optional(),
  /** If this mapping supersedes or was merged with another, the predecessor ID. */
  predecessorMappingId: Id.nullable().optional(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  createdBy: Id.nullable(),
  updatedBy: Id.nullable(),
});
export type ExternalIdMapping = z.infer<typeof ExternalIdMapping>;

// ---------------------------------------------------------------------------
// External entity schemas — the simulator/provider's view of the world
// ---------------------------------------------------------------------------

/**
 * A site as known to an external integration provider.
 * Stored in SimTable under PK=EXT#{externalTenantId}#INT#{integrationId}, SK=SITE#{externalSiteId}.
 */
export const ExternalSite = z.object({
  externalSiteId: z.string().min(1),
  integrationId: z.string().min(1),
  externalTenantId: z.string().min(1),
  name: z.string().max(200).nullable(),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  metadata: z.record(z.unknown()).optional(),
});
export type ExternalSite = z.infer<typeof ExternalSite>;

/**
 * An asset as known to an external integration provider.
 * Stored in SimTable under PK=EXT#{externalTenantId}#INT#{integrationId}, SK=ASSET#{externalAssetId}.
 */
export const ExternalAsset = z.object({
  externalAssetId: z.string().min(1),
  externalSiteId: z.string().nullable().optional(),
  integrationId: z.string().min(1),
  externalTenantId: z.string().min(1),
  name: z.string().max(200).nullable(),
  assetType: z.string().nullable().optional(),
  metadata: z.record(z.unknown()).optional(),
});
export type ExternalAsset = z.infer<typeof ExternalAsset>;

/**
 * A device as known to an external integration provider.
 * Stored in SimTable under PK=EXT#{externalTenantId}#INT#{integrationId}, SK=DEVICE#{externalDeviceId}.
 */
export const ExternalDevice = z.object({
  externalDeviceId: z.string().min(1),
  externalAssetId: z.string().nullable().optional(),
  externalSiteId: z.string().nullable().optional(),
  integrationId: z.string().min(1),
  externalTenantId: z.string().min(1),
  /** Profile from the DeviceTypeProfile catalog. Null falls back to generic.v1. */
  profileId: z.string().nullable().optional(),
  onlineState: z.enum(["Online", "Offline", "Degraded", "Unknown"]).optional(),
  batteryLevel: z.number().nullable().optional(),
  signalStrength: z.number().nullable().optional(),
  faultCode: z.string().nullable().optional(),
  lat: z.number().nullable().optional(),
  lng: z.number().nullable().optional(),
  lastSeenAt: z.string().optional(),
  /** Command types received by this device; used by onCommand scenario steps. */
  receivedCommands: z.array(z.string()).default([]),
  metadata: z.record(z.unknown()).optional(),
});
export type ExternalDevice = z.infer<typeof ExternalDevice>;

export const UnmappedExternalEntity = z.object({
  unmappedExternalEntityId: Id,
  tenantId: Id,
  integrationId: Id,
  externalEntityType: ExternalEntityType,
  externalId: z.string().min(1).max(256),
  displayName: z.string().max(200).nullable(),
  payload: z.record(z.unknown()).nullable(),
  firstSeenAt: IsoDateTime,
  lastSeenAt: IsoDateTime,
});
export type UnmappedExternalEntity = z.infer<typeof UnmappedExternalEntity>;

export const IntegrationConnectionCreateInput = z.object({
  name: z.string().min(1).max(160),
  integrationType: IntegrationType,
  adapterId: z.string().min(1).max(120).optional(),
  adapterVersion: z.string().min(1).max(50).optional(),
  config: z.union([IntegrationConfig, z.record(z.unknown())]).default({}),
});
export type IntegrationConnectionCreateInput = z.infer<
  typeof IntegrationConnectionCreateInput
>;

export const IntegrationConnectionUpdateInput = z
  .object({
    name: z.string().min(1).max(160),
    status: IntegrationStatus,
    adapterId: z.string().min(1).max(120).nullable(),
    adapterVersion: z.string().min(1).max(50).nullable(),
    config: z.record(z.unknown()),
  })
  .partial();
export type IntegrationConnectionUpdateInput = z.infer<
  typeof IntegrationConnectionUpdateInput
>;

export const IntegrationListFilters = PageQuery.extend({
  status: IntegrationStatus.optional(),
  integrationType: IntegrationType.optional(),
});
export type IntegrationListFilters = z.infer<typeof IntegrationListFilters>;

export const IntegrationEventListFilters = PageQuery.extend({
  eventType: IntegrationEventType.optional(),
  occurredFrom: IsoDateTime.optional(),
  occurredTo: IsoDateTime.optional(),
});
export type IntegrationEventListFilters = z.infer<
  typeof IntegrationEventListFilters
>;

export const IntegrationForceRefreshPollResult = z.object({
  deviceUpdateCount: z.number().int().min(0),
  alertUpdateCount: z.number().int().min(0),
  maintenanceUpdateCount: z.number().int().min(0),
  healthEventCount: z.number().int().min(0),
});
export type IntegrationForceRefreshPollResult = z.infer<typeof IntegrationForceRefreshPollResult>;

export const IntegrationForceRefreshResponse = z.object({
  eventId: Id,
  /** Completed/Failed for synchronous MVP. Queued reserved for future async path. */
  status: z.enum(["Queued", "Completed", "Failed"]),
  pollResult: IntegrationForceRefreshPollResult.optional(),
  error: z.string().nullable(),
});
export type IntegrationForceRefreshResponse = z.infer<
  typeof IntegrationForceRefreshResponse
>;

// ---------------------------------------------------------------------------
// Adapter Registry — certified adapter definitions and versions
// ---------------------------------------------------------------------------

/**
 * High-level category for an adapter. Used to group adapters in the registry
 * and drive which config schema is required.
 */
export const AdapterCategory = z.enum([
  "Simulator",
  "ManufacturerPoll",
  "WebhookReceiver",
  "NetworkServer",
  "CarrierPlatform",
  "VmsVideoMetadata",
  "FileImport",
  "DeviceDirect",
  "Other",
]);
export type AdapterCategory = z.infer<typeof AdapterCategory>;

/**
 * Transport modes the adapter supports.
 */
export const AdapterTransport = z.enum([
  "HttpPoll",
  "WebhookPush",
  "Mqtt",
  "LwM2M",
  "OpcUa",
  "FileS3",
  "DeviceMtls",
]);
export type AdapterTransport = z.infer<typeof AdapterTransport>;

/**
 * Authentication modes the adapter supports for outbound calls.
 */
export const AdapterAuthMode = z.enum([
  "SharedSecret",
  "ApiKey",
  "OAuth2ClientCredentials",
  "Mtls",
  "None",
]);
export type AdapterAuthMode = z.infer<typeof AdapterAuthMode>;

/**
 * Platform-level adapter definition. One record per adapterId. Immutable
 * after certification; new capabilities require a new version.
 */
export const AdapterDefinition = z.object({
  adapterId: z.string().min(1).max(120),
  name: z.string().min(1).max(200),
  category: AdapterCategory,
  description: z.string().max(2000).optional(),
  vendorName: z.string().max(200).optional(),
  supportedTransports: z.array(AdapterTransport).min(1),
  supportedAuthModes: z.array(AdapterAuthMode).min(1),
  /**
   * JSON Schema (as plain object) that the adapter's IntegrationConnection.config
   * must satisfy. Used for runtime validation in operations container.
   */
  configSchema: z.record(z.unknown()).optional(),
  /** Profile IDs from DEVICE_PROFILES this adapter is certified to produce. */
  compatibleProfileIds: z.array(z.string().min(1)).default([]),
  /** Whether this adapter is available for self-service tenant provisioning. */
  isSelfService: z.boolean().default(false),
  /** Whether this adapter has passed platform certification. */
  isCertified: z.boolean().default(false),
  latestVersion: z.string().min(1).max(50),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type AdapterDefinition = z.infer<typeof AdapterDefinition>;

/**
 * An immutable version snapshot of an AdapterDefinition.
 * The platform pins an IntegrationConnection to a specific adapterVersion.
 */
export const AdapterVersion = z.object({
  adapterId: z.string().min(1).max(120),
  adapterVersion: z.string().min(1).max(50),
  releaseNotes: z.string().max(2000).optional(),
  /** Whether this version is still recommended (false = use a newer version). */
  isSupported: z.boolean().default(true),
  configSchema: z.record(z.unknown()).optional(),
  compatibleProfileIds: z.array(z.string().min(1)).default([]),
  releasedAt: IsoDateTime,
  deprecatedAt: IsoDateTime.nullable().optional(),
});
export type AdapterVersion = z.infer<typeof AdapterVersion>;

// ---------------------------------------------------------------------------
// Mapping workflow input schemas
// ---------------------------------------------------------------------------

/** Approve an unmapped entity and link it to an existing internal entity. */
export const MappingLinkInput = z.object({
  integrationId: Id,
  externalEntityType: ExternalEntityType,
  externalId: z.string().min(1).max(256),
  internalEntityId: Id,
});
export type MappingLinkInput = z.infer<typeof MappingLinkInput>;

/** Approve an unmapped entity and create a new internal entity to link it to. */
export const MappingCreateAndLinkInput = z.object({
  integrationId: Id,
  externalEntityType: ExternalEntityType,
  externalId: z.string().min(1).max(256),
  /** Fields used to create the new internal device/asset/site record. */
  createPayload: z.record(z.unknown()),
});
export type MappingCreateAndLinkInput = z.infer<typeof MappingCreateAndLinkInput>;

/** Reject an unmapped entity — suppress from the unmapped queue. */
export const MappingRejectInput = z.object({
  integrationId: Id,
  externalEntityType: ExternalEntityType,
  externalId: z.string().min(1).max(256),
  reason: z.string().max(2000).optional(),
});
export type MappingRejectInput = z.infer<typeof MappingRejectInput>;

/** Bulk-map multiple unmapped entities in a single operation. */
export const MappingBulkLinkInput = z.object({
  mappings: z.array(MappingLinkInput).min(1).max(100),
});
export type MappingBulkLinkInput = z.infer<typeof MappingBulkLinkInput>;

export const IntegrationConnectionListResponse = Page(IntegrationConnection);
export type IntegrationConnectionListResponse = z.infer<
  typeof IntegrationConnectionListResponse
>;

export const IntegrationEventListResponse = Page(IntegrationEvent);
export type IntegrationEventListResponse = z.infer<
  typeof IntegrationEventListResponse
>;

export const ExternalIdMappingListResponse = Page(ExternalIdMapping);
export type ExternalIdMappingListResponse = z.infer<
  typeof ExternalIdMappingListResponse
>;

export const UnmappedExternalEntityListResponse = Page(UnmappedExternalEntity);
export type UnmappedExternalEntityListResponse = z.infer<
  typeof UnmappedExternalEntityListResponse
>;
